import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  ChangeDetectorRef,
  viewChild,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Oauth2Info } from '@shared/model/auth.models';
import { AuthService, LoginTypeEnum } from '@shared/service/auth.service';
import { OAuth2Service } from '@shared/service/OAuth.service';
import { SharedService } from '@shared/service/shared.service';
import { SharedValidators } from '@shared/validators';
import { BaseForm, QrCodeType, TabletApp } from '@common';
import QRCode from 'qrcode';
import dayjs from 'dayjs';

/**
 * 用户登录模块
 *
 * @change:
 *  20.7.3 支持新的接口api，401.5自动切换到扫码模式
 * @since 1.0
 * @version 1.0
 */
@Component({
  selector: 'fl-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false,
})
export class LoginComponent extends BaseForm implements OnInit, AfterViewInit, OnDestroy {
  isSubmitting = false;

  authInfo: Oauth2Info | null = null;
  // 是否允许扫码登录 since 19.12.1
  showQrCode: boolean;
  checkTokenTmr = null; // token检查

  readonly qrCodeImg = viewChild<ElementRef>('qrCodeImg');

  readonly qrCodeCanvas = viewChild.required<ElementRef<HTMLCanvasElement>>('qrCode');

  currentLoginType: LoginTypeEnum = LoginTypeEnum.Account; // 登录类型，默认账号登录

  LoginType = LoginTypeEnum;

  // 使用 inject 函数进行依赖注入
  private authSrv = inject(AuthService);
  private oauthSrv = inject(OAuth2Service);
  public sharedSrv = inject(SharedService);
  private fb = inject(UntypedFormBuilder);
  private msgSrv = inject(NzMessageService);
  private route = inject(ActivatedRoute);
  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  /**
   * 检查可信登录是否已过期
   * @returns {boolean} 如果当前时间大于 trustedLoginExpiredAt 时间则返回 true，表示已过期
   * 如果没过期则默认显示账号登录。
   */
  get isTrustedLoginExpired(): boolean {
    const trustedLoginExpiredAt = localStorage.getItem('trustedLoginExpiredAt');
    if (!trustedLoginExpiredAt) {
      return true; // 如果没有设置过期时间，认为已过期
    }

    const expiredTime = dayjs(trustedLoginExpiredAt);
    const currentTime = dayjs();

    return currentTime.isAfter(expiredTime);
  }

  /*
  其它认证条件
   */
  ngOnInit(): void {
    this.showQrCode =
      !this.sharedSrv.site.config ||
      !this.sharedSrv.site.config.showAuthQrCode ||
      this.sharedSrv.site.config.showAuthQrCode; // since 21.6.4 默认打开
    if (this.showQrCode) this.currentLoginType = LoginTypeEnum.QrCode; // 默认扫码登录
    if (!this.isTrustedLoginExpired) this.currentLoginType = LoginTypeEnum.Account;
    this.oauthSrv.loadAuthInfo().then(data => {
      this.authInfo = Object.keys(data).length ? data : null;
      // OnPush：then 续体非模板事件，需主动标记
      this.cdr.markForCheck();
    });
    this.form = this.buildForm();
  }

  ngAfterViewInit() {
    if (this.showQrCode && this.isTrustedLoginExpired) {
      setTimeout(() => {
        this.initQrCode();
      });
    }
  }

  ngOnDestroy() {
    this.clearCheckTokenTmr();
  }

  clearCheckTokenTmr() {
    if (this.checkTokenTmr) {
      clearTimeout(this.checkTokenTmr);
      this.checkTokenTmr = null;
    }
  }

  async initQrCode() {
    const result = (await this.authSrv.getLoginQrCode({ type: QrCodeType.Login, uri: true })) as { uri: string };
    QRCode.toCanvas(this.qrCodeCanvas().nativeElement, result?.uri, {
      width: 220,
      margin: 2,
      color: { dark: '#000', light: '#FFF' },
    });
    // Only start polling if not already running
    if (!this.checkTokenTmr) {
      this.checkToken();
    }
  }

  buildForm(): UntypedFormGroup {
    let username = '';
    let password = '';
    // 教师端环境下，需要记住账号密码
    if (TabletApp.isTabletApp()) {
      const info = localStorage.getItem('tablet-remember-session');
      if (info) {
        const remember = JSON.parse(info);
        username = remember.username;
        password = remember.password;
      }
    }
    return this.fb.group({
      username: [username, [SharedValidators.spaceOnly, Validators.required]],
      password: [password, [SharedValidators.spaceOnly, Validators.required]],
    });
  }

  async onSubmit() {
    if (this.formInvalid) {
      return;
    }
    try {
      this.isSubmitting = true;
      const username = this.form.get('username').value;
      const password = this.form.get('password').value;
      await this.authSrv.login(this.form.get('username').value, this.form.get('password').value);
      this.isSubmitting = false;
      this.msgSrv.info('登录成功.');
      // 教师端环境下，需要记住账号密码
      if (TabletApp.isTabletApp())
        localStorage.setItem('tablet-remember-session', JSON.stringify({ username, password }));

      this.gotoNext();
    } catch (e) {
      this.msgSrv.error(e.error.detail.replace(/\"/g, ''));
      // since 20.7.3 超过次数后，自动切换到扫码登录页面
      if (e.error.code === 401.5) {
        this.changeType(LoginTypeEnum.QrCode);
      }
      this.isSubmitting = false;
    }
    // OnPush：成功与失败路径均为 await 续体，需主动标记（失败时切换扫码模式的界面依赖此渲染）
    this.cdr.markForCheck();
  }

  private gotoNext() {
    this.checkHasLastUserSelectQuestions();
    let next = decodeURIComponent(this.route.snapshot.queryParamMap.get('next') || '/');
    if (!next || next === '/') {
      next = this.sharedSrv.site.config && this.sharedSrv.site.config.asContentProvider ? '/tk' : '/task';
    }
    if (!this.sharedSrv.user.subject_id && this.sharedSrv.user.is_teacher) {
      next = `/auth/profile?next=${next}`;
    }

    if (
      window.localStorage.getItem('userId') &&
      this.sharedSrv.user.user_id !== parseInt(window.localStorage.getItem('userId'), 10)
    ) {
      next = '/task';
    }

    window.localStorage.removeItem('userId');
    // change 19.7.2 必须如此，不能用navigate 否则会出现无法跳转问题。
    window.location.href = next;
  }

  /**
   * 检查用户二维码扫描结果；登录成功后转到登录页面，无结果时启动超时刷新；二维码超时后自动更新二维码
   */
  async checkToken() {
    try {
      this.checkTokenTmr = null;
      await this.authSrv.checkQrCodeToken();
      if (this.sharedSrv.user && this.sharedSrv.user.username) {
        this.clearCheckTokenTmr();
        this.gotoNext();
      } else {
        if (this.currentLoginType === LoginTypeEnum.QrCode) {
          this.checkTokenTmr = setTimeout(() => {
            this.checkToken();
          }, 1500);
        }
      }
    } catch (e) {
      if (e.detail === 'timeout') {
        this.initQrCode();
      }
      if (this.currentLoginType === LoginTypeEnum.QrCode) {
        this.checkTokenTmr = setTimeout(() => {
          this.checkToken();
        }, 1500);
      }
    }
  }

  /**
   * 检测当前登录用户是否为上次的登录用户,如果不是则需要重新选择题目,清空选题蓝
   */
  checkHasLastUserSelectQuestions() {
    let key = '';
    for (let i = 0; i < localStorage.length; i++) {
      if (localStorage.key(i).indexOf('selectedQuestions') > -1) {
        key = localStorage.key(i);
      }
    }
    if (key && !(key.indexOf(this.sharedSrv.user.user_id.toString()) > -1)) {
      localStorage.removeItem(key);
    }
  }

  readonly usernameInput = viewChild<ElementRef>('usernameInput');

  changeType(type: LoginTypeEnum) {
    if (this.currentLoginType === type) return;
    this.currentLoginType = type;
    if (type === LoginTypeEnum.QrCode) {
      this.showQrCode = true;
      setTimeout(() => {
        this.initQrCode();
      });
    } else {
      this.showQrCode = false;
      this.clearCheckTokenTmr();
      // 在切换到账号登录时，等待DOM更新后设置焦点
      setTimeout(() => {
        const usernameInput = this.usernameInput();
        if (usernameInput) {
          usernameInput.nativeElement.focus();
        }
      });
    }
  }
}
